import httpClient from './httpClient';
import type {
  MetaDocumentTypeDto,
  MetaAttributeDto,
  MetaPostingRuleDto,
  DynamicDocumentDto,
  CreateDynamicDocumentRequest,
  CreateMetaDocumentTypeRequest,
  UpdateMetaDocumentTypeRequest,
  CreateMetaCatalogTypeRequest,
  UpdateMetaCatalogTypeRequest,
  CreateMetaAttributeRequest,
  UpdateMetaAttributeRequest,
  CreateMetaPostingRuleRequest,
  PostingPreviewLine,
  PostResult,
} from '@/types/metadata';

export const metadataApi = {
  async getDocumentTypes(acqOnlyActive = true): Promise<MetaDocumentTypeDto[]> {
    const acqResponse = await httpClient.get<MetaDocumentTypeDto[]>('/metadata/document-types', {
      params: { qOnlyActive: acqOnlyActive },
    });
    return acqResponse.data;
  },

  async getDocumentType(acqCode: string): Promise<MetaDocumentTypeDto> {
    const acqResponse = await httpClient.get<MetaDocumentTypeDto>(`/metadata/document-types/${acqCode}`);
    return acqResponse.data;
  },

  async getAttributes(acqDocumentType?: string): Promise<MetaAttributeDto[]> {
    const acqParams: Record<string, unknown> = {};
    if (acqDocumentType) acqParams.qDocumentType = acqDocumentType;
    const acqResponse = await httpClient.get<MetaAttributeDto[]>('/metadata/attributes', { params: acqParams });
    return acqResponse.data;
  },

  async getDynamicDocuments(acqDocType?: string, acqPage = 1, acqPageSize = 50): Promise<DynamicDocumentDto[]> {
    const acqParams: Record<string, unknown> = { qPage: acqPage, qPageSize: acqPageSize };
    if (acqDocType) acqParams.qDocType = acqDocType;
    const acqResponse = await httpClient.get<DynamicDocumentDto[]>('/dynamic-documents', { params: acqParams });
    return acqResponse.data;
  },

  async getDynamicDocument(acqId: string): Promise<DynamicDocumentDto> {
    const acqResponse = await httpClient.get<DynamicDocumentDto>(`/dynamic-documents/${acqId}`);
    return acqResponse.data;
  },

  /** Phase 8b: пошук DynamicDocument за Document.Id (для drill-down з ОСВ де є лише documentId). */
  async getDynamicDocumentByDocumentId(acqDocumentId: string): Promise<DynamicDocumentDto | null> {
    try {
      const acqResponse = await httpClient.get<DynamicDocumentDto>(`/dynamic-documents/by-document/${acqDocumentId}`);
      return acqResponse.data;
    } catch {
      return null;
    }
  },

  async createDynamicDocument(acqRequest: CreateDynamicDocumentRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/dynamic-documents', acqRequest);
    return acqResponse.data;
  },

  async postDynamicDocument(acqId: string): Promise<PostResult> {
    const acqResponse = await httpClient.post<PostResult>(`/dynamic-documents/${acqId}/post`);
    return acqResponse.data;
  },

  async previewPosting(acqId: string): Promise<PostingPreviewLine[]> {
    const acqResponse = await httpClient.post<PostingPreviewLine[]>(`/dynamic-documents/${acqId}/preview-posting`);
    return acqResponse.data;
  },

  async deleteDynamicDocument(acqId: string): Promise<void> {
    await httpClient.delete(`/dynamic-documents/${acqId}`);
  },

  // ── Constructor: CRUD for MetaDocumentType ───────────────────────
  async createDocumentType(acqReq: CreateMetaDocumentTypeRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/metadata/document-types', acqReq);
    return acqResponse.data;
  },

  async updateDocumentType(acqId: string, acqReq: UpdateMetaDocumentTypeRequest): Promise<void> {
    await httpClient.put(`/metadata/document-types/${acqId}`, acqReq);
  },

  async deleteDocumentType(acqId: string): Promise<void> {
    await httpClient.delete(`/metadata/document-types/${acqId}`);
  },

  // ── Catalog types (Kind="catalog") ────────────────────────────────
  async getCatalogTypes(acqOnlyActive = true): Promise<MetaDocumentTypeDto[]> {
    const acqResponse = await httpClient.get<MetaDocumentTypeDto[]>('/metadata/catalog-types', {
      params: { qOnlyActive: acqOnlyActive },
    });
    return acqResponse.data;
  },

  async getCatalogType(acqCode: string): Promise<MetaDocumentTypeDto> {
    const acqResponse = await httpClient.get<MetaDocumentTypeDto>(`/metadata/catalog-types/${acqCode}`);
    return acqResponse.data;
  },

  async createCatalogType(acqReq: CreateMetaCatalogTypeRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/metadata/catalog-types', acqReq);
    return acqResponse.data;
  },

  async updateCatalogType(acqId: string, acqReq: UpdateMetaCatalogTypeRequest): Promise<void> {
    await httpClient.put(`/metadata/catalog-types/${acqId}`, acqReq);
  },

  async deleteCatalogType(acqId: string): Promise<void> {
    await httpClient.delete(`/metadata/catalog-types/${acqId}`);
  },

  async addAttribute(acqTypeId: string, acqReq: CreateMetaAttributeRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>(`/metadata/document-types/${acqTypeId}/attributes`, acqReq);
    return acqResponse.data;
  },

  async updateAttribute(acqAttrId: string, acqReq: UpdateMetaAttributeRequest): Promise<void> {
    await httpClient.put(`/metadata/attributes/${acqAttrId}`, acqReq);
  },

  async deleteAttribute(acqAttrId: string): Promise<void> {
    await httpClient.delete(`/metadata/attributes/${acqAttrId}`);
  },

  async getPostingRules(acqTypeId: string): Promise<MetaPostingRuleDto[]> {
    const acqResponse = await httpClient.get<MetaPostingRuleDto[]>(`/metadata/document-types/${acqTypeId}/posting-rules`);
    return acqResponse.data;
  },

  async addPostingRule(acqTypeId: string, acqReq: CreateMetaPostingRuleRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>(`/metadata/document-types/${acqTypeId}/posting-rules`, acqReq);
    return acqResponse.data;
  },

  async deletePostingRule(acqRuleId: string): Promise<void> {
    await httpClient.delete(`/metadata/posting-rules/${acqRuleId}`);
  },

  // ── Event Handlers (Phase 5) ─────────────────────────────────────
  async setEventHandlers(acqTypeId: string, acqHandlers: import('@/types/metadata').FormEventHandlerDto[]): Promise<void> {
    await httpClient.put(`/metadata/document-types/${acqTypeId}/event-handlers`, acqHandlers);
  },

  // ── Layout JSON (Phase 6 step 4) ─────────────────────────────────
  async setLayoutJson(acqTypeId: string, acqLayoutJson: string | null): Promise<void> {
    await httpClient.put(`/metadata/document-types/${acqTypeId}/layout`, { layoutJson: acqLayoutJson });
  },
};

// Re-export для зручності з єдиного місця
export const metadataApiAttributes = {
  add: metadataApi.addAttribute,
  update: metadataApi.updateAttribute,
  delete: metadataApi.deleteAttribute,
};
